"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode
} from "react";

type Language = "sv" | "en";

type LanguageContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
  toggleLanguage: () => void;
};

const LanguageContext = createContext<LanguageContextValue>({
  language: "sv",
  // Default no-op implementations – real ones are provided by the provider
  setLanguage: () => {},
  toggleLanguage: () => {}
});

const STORAGE_KEY = "bilspektrum-language";

function getPreferredLanguage(): Language {
  const stored = window.localStorage.getItem(STORAGE_KEY) as Language | null;
  if (stored === "sv" || stored === "en") return stored;
  return (navigator.language || "").toLowerCase().startsWith("sv") ? "sv" : "en";
}

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  // Always start in Swedish so the first client render matches the server HTML
  // (otherwise React throws a hydration error); switch right after mounting.
  const [language, setLanguage] = useState<Language>("sv");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setLanguage(getPreferredLanguage());
    setReady(true);
  }, []);

  // Persist language choice (not before the stored choice has been read)
  useEffect(() => {
    if (!ready) return;
    window.localStorage.setItem(STORAGE_KEY, language);
    // Keep <html lang="..."> in sync for accessibility and SEO
    document.documentElement.lang = language;
  }, [language, ready]);

  const toggleLanguage = useCallback(() => {
    setLanguage((prev) => (prev === "sv" ? "en" : "sv"));
  }, []);

  const value: LanguageContextValue = {
    language,
    setLanguage,
    toggleLanguage
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  return ctx;
};


